import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type { NavigationMergeRulesSideSheet } from '@gitbook/app-navigation';
import {
    Badge,
    Button,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    Heading,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';

import {
    MergeRulesConfigurationEditor,
    MergeRulesUpgradeAlert,
    useMergeRulesConfigurationEditor,
} from '../../../Components';
import { useSetAnalyticsScreen, useSideSheetNavigation } from '../../../navigation';
import { apiClient, useUpgrade } from '../../../state';

/**
 * Dialog to configure the merge rule for a space.
 */
export function SpaceMergeRulesDialog(props: { space: api.Space }) {
    const { space } = props;

    const { opened, close } = useSideSheetNavigation<NavigationMergeRulesSideSheet>('merge-rule');

    return (
        <DialogRoot isOpen={opened} onOpenChange={close}>
            <Dialog size={{ width: 640 }}>
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <Heading>Configure merge rule for this space</Heading>
                        <Badge kind="beta" size="small" label="Beta" />
                    </div>
                    <Description>
                        Define the rules for merging change requests into this space.
                    </Description>
                </DialogHeader>
                <SpaceMergeRulesDialogContent space={space} />
            </Dialog>
        </DialogRoot>
    );
}

function SpaceMergeRulesDialogContent(props: { space: api.Space }) {
    const { space } = props;

    const upgrade = useUpgrade('change-requests-merge-rules');
    const [, withDialogAction] = useDialogAction();
    const configEditor = useMergeRulesConfigurationEditor(space.mergeRules);

    useSetAnalyticsScreen(
        analytics.createSpaceEvent(space, {
            screen: 'space_merge_rules',
        }),
        [space.id]
    );

    const onSubmit = async () => {
        await withDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                await apiClient.spaces.updateSpaceById(space.id, {
                    mergeRules: configEditor.configuration,
                });
                toast.success('Merge rules updated');
                await close();
            }, 'Error while updating merge rules');
        });
    };

    return (
        <>
            <DialogBody className="flex flex-col gap-6">
                {upgrade ? <MergeRulesUpgradeAlert upgrade={upgrade} /> : null}
                <MergeRulesConfigurationEditor
                    configuration={configEditor.configuration}
                    onChange={configEditor.onChange}
                    allowInherit
                />
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Button
                    label="Save"
                    onPress={onSubmit}
                    disabled={!configEditor.hasChanges}
                    {...(configEditor.hasChanges ? upgrade : undefined)}
                />
            </DialogFooter>
        </>
    );
}
